import React, { useState, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useContentTasks } from '../state/data/task';
import { TASK_KEYS } from '../state/data/task/keys';
import {
  useContentCatalog,
  useScheduleContentTask,
  useScheduleCustomContentTask,
  useCancelContentTask,
  useReactivateContentTask,
  useRescheduleContentTask,
} from '../state/data/content';
import { interpretScheduleResult } from '../logic/content';

/* ── Constants ─────────────────────────────────────────────────── */

const TRACK_COLORS = {
  onboarding:         '#6366F1',
  'device-mastery':   '#8B5CF6',
  hypertension:       '#EF4444',
  'diabetes-care':    '#F97316',
  'weight-management':'#22C55E',
  'heart-health':     '#EC4899',
  medication:         '#3B82F6',
  'sleep-health':     '#A855F7',
  motivation:         '#EAB308',
  'journey-milestones':'#14B8A6',
  cholesterol:        '#F59E0B',
  'kidney-health':    '#0EA5E9',
  'lifestyle-spine':  '#10B981',
  challenges:         '#F43F5E',
  tools:              '#64748B',
  'smoking-cessation':'#78716C',
  'alcohol-balance':  '#B45309',
  custom:             '#6B7280',
};

const STATUS_META = {
  completed:   { label: 'Completed',  color: '#22C55E', icon: '\u2713' },
  in_progress: { label: 'In progress',color: '#3B82F6', icon: '\u25CF' },
  pending:     { label: 'Pending',    color: '#9CA3AF', icon: '\u25CB' },
  scheduled:   { label: 'Scheduled',  color: '#9CA3AF', icon: '\u25CB' },
  cancelled:   { label: 'Cancelled',  color: '#6B7280', icon: '\u2715' },
  missed:      { label: 'Missed',     color: '#EF4444', icon: '!' },
  overdue:     { label: 'Overdue',    color: '#EF4444', icon: '!' },
};

const PHASE_LABELS = {
  1: 'Foundation',
  2: 'Education',
  3: 'Optimization',
  4: 'Sustaining',
  5: 'Graduating',
};

const CONTENT_BASE_URL = 'https://sparks.withings.com/sparks/access-content/content/';

/* ── Helpers ───────────────────────────────────────────────────── */

function parseCtx(task) {
  if (!task) return {};
  if (task.context_json && typeof task.context_json === 'object') return task.context_json;
  try { return JSON.parse(task.context_json || '{}'); } catch { return {}; }
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtMonthYear(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function groupByMonth(tasks) {
  const groups = {};
  for (const t of tasks) {
    const key = fmtMonthYear(t.available_at || t.due_at);
    if (!groups[key]) groups[key] = [];
    groups[key].push(t);
  }
  return groups;
}

function trackColor(track) {
  return TRACK_COLORS[track] || '#6B7280';
}

function getContentUrl(task) {
  const ctx = parseCtx(task);
  if (ctx.url) return ctx.url;
  if (ctx.slug && !ctx.slug.startsWith('custom-')) return CONTENT_BASE_URL + ctx.slug + '.html';
  return null;
}

function getContentUrlFromCatalog(item) {
  if (!item) return null;
  if (item.url_path?.startsWith('http')) return item.url_path;
  if (item.url_path) return 'https://sparks.withings.com/' + item.url_path;
  if (item.slug) return CONTENT_BASE_URL + item.slug + '.html';
  return null;
}

/** Compute the next available slot date (2 days after last scheduled task, or tomorrow) */
function computeNextSlotDate(existingTasks, offset = 0) {
  const futureTasks = existingTasks
    .filter(t => ['scheduled', 'pending'].includes(t.status))
    .sort((a, b) => new Date(b.available_at || b.due_at) - new Date(a.available_at || a.due_at));

  let base;
  if (futureTasks.length > 0) {
    base = new Date(futureTasks[0].available_at || futureTasks[0].due_at);
    base.setDate(base.getDate() + 2);
  } else {
    base = new Date();
    base.setDate(base.getDate() + 1);
  }

  if (offset > 0) {
    base.setDate(base.getDate() + (offset * 2));
  }

  // Floor to tomorrow minimum
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  if (base < tomorrow) base = tomorrow;

  return base.toISOString().split('T')[0];
}

/**
 * Unified per-track stats combining catalog size + scheduled task counts.
 * Every track that appears in the catalog OR has any task gets an entry.
 */
function computeTrackCatalogStats(catalog, tasks) {
  const map = {};
  const bucket = () => ({ catalogTotal: 0, scheduled: 0, completed: 0, pending: 0 });

  for (const item of catalog) {
    if (!item.track) continue;
    if (!map[item.track]) map[item.track] = bucket();
    map[item.track].catalogTotal++;
  }

  for (const t of tasks) {
    if (t.status === 'cancelled') continue;
    const track = parseCtx(t).track;
    if (!track || track === 'custom') continue;
    if (!map[track]) map[track] = bucket();
    map[track].scheduled++;
    if (t.status === 'completed') map[track].completed++;
    if (['scheduled', 'pending'].includes(t.status)) map[track].pending++;
  }

  return map;
}

/** Compute a new date when dropping a task between two neighbors */
function computeDropDate(beforeTask, afterTask) {
  const before = beforeTask ? new Date(beforeTask.available_at || beforeTask.due_at) : null;
  const after = afterTask ? new Date(afterTask.available_at || afterTask.due_at) : null;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  let result;
  if (before && after) {
    result = new Date((before.getTime() + after.getTime()) / 2);
    if (result.toDateString() === before.toDateString()) {
      result = new Date(before);
      result.setDate(result.getDate() + 1);
    }
  } else if (before && !after) {
    result = new Date(before);
    result.setDate(result.getDate() + 2);
  } else if (!before && after) {
    result = new Date(after);
    result.setDate(result.getDate() - 1);
  } else {
    result = tomorrow;
  }

  if (result < tomorrow) result = tomorrow;
  return result.toISOString().split('T')[0];
}

/* ── Styles ────────────────────────────────────────────────────── */

const s = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 },
  statRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  stat: (bg) => ({ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 4, background: bg + '18', color: bg }),
  filterRow: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
  select: { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--wi-divider)', background: 'var(--wi-background-secondary)', color: 'var(--wi-text-primary)', fontSize: 12, fontFamily: 'var(--wi-font-family)', outline: 'none', cursor: 'pointer' },
  addBtn: { padding: '6px 14px', borderRadius: 8, border: 'none', background: 'var(--wi-action-primary)', color: 'var(--wi-on-action-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--wi-font-family)' },
  addBtnDisabled: { padding: '6px 14px', borderRadius: 8, border: 'none', background: 'var(--wi-background-tertiary)', color: 'var(--wi-text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'not-allowed', fontFamily: 'var(--wi-font-family)' },
  monthHeader: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--wi-text-secondary)', padding: '12px 0 6px', borderBottom: '1px solid var(--wi-divider)', marginBottom: 4 },
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--wi-divider)', transition: 'background 100ms ease' },
  dateCol: { width: 52, flexShrink: 0, fontSize: 12, fontWeight: 500, color: 'var(--wi-text-secondary)' },
  trackBadge: (c) => ({ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: c + '20', color: c, whiteSpace: 'nowrap' }),
  title: { flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--wi-text-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  titleCancelled: { flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--wi-text-secondary)', textDecoration: 'line-through', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  formatBadge: { fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--wi-background-tertiary)', color: 'var(--wi-text-secondary)', whiteSpace: 'nowrap' },
  statusBadge: (c) => ({ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: c + '18', color: c, whiteSpace: 'nowrap' }),
  cancelBtn: { padding: '3px 8px', borderRadius: 6, border: '1px solid var(--wi-divider)', background: 'none', color: 'var(--wi-text-secondary)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--wi-font-family)', whiteSpace: 'nowrap' },
  cancelConfirm: { display: 'flex', alignItems: 'center', gap: 6 },
  confirmBtn: { padding: '3px 8px', borderRadius: 6, border: 'none', background: '#EF4444', color: '#fff', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--wi-font-family)' },
  emptyState: { textAlign: 'center', padding: '40px 20px', color: 'var(--wi-text-secondary)', fontSize: 13 },
  // Read button
  readBtn: { padding: '3px 8px', borderRadius: 6, border: '1px solid var(--wi-action-primary)', background: 'none', color: 'var(--wi-action-primary)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--wi-font-family)', whiteSpace: 'nowrap' },
  // Drag handle
  dragHandle: { cursor: 'grab', color: 'var(--wi-text-secondary)', userSelect: 'none', padding: '4px 3px', flexShrink: 0, borderRadius: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, lineHeight: 1, transition: 'background 120ms ease, color 120ms ease' },
  dragDot: { width: 3, height: 3, borderRadius: '50%', background: 'currentColor' },
  dragDotRow: { display: 'flex', gap: 3 },
  dropIndicator: { height: 2, background: 'var(--wi-action-primary)', margin: '0', borderRadius: 1 },
  // Modal styles
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--wi-background-primary)', borderRadius: 16, width: '90%', maxWidth: 640, maxHeight: '80vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--wi-divider)' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--wi-divider)' },
  modalClose: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--wi-text-secondary)', fontFamily: 'var(--wi-font-family)' },
  modalBody: { flex: 1, overflow: 'auto', padding: '12px 20px' },
  modalFooter: { padding: '12px 20px', borderTop: '1px solid var(--wi-divider)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  catalogItem: (selected) => ({ padding: '10px 12px', borderRadius: 8, border: selected ? '2px solid var(--wi-action-primary)' : '1px solid var(--wi-divider)', cursor: 'pointer', marginBottom: 6, background: selected ? 'var(--wi-background-info)' : 'var(--wi-background-secondary)' }),
  catalogItemDisabled: { padding: '10px 12px', borderRadius: 8, border: '1px solid var(--wi-divider)', marginBottom: 6, background: 'var(--wi-background-tertiary)', opacity: 0.5, cursor: 'default' },
  searchInput: { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--wi-divider)', fontSize: 12, fontFamily: 'var(--wi-font-family)', outline: 'none', flex: 1, minWidth: 120, background: 'var(--wi-background-secondary)', color: 'var(--wi-text-primary)' },
  dateInput: { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--wi-divider)', fontSize: 12, fontFamily: 'var(--wi-font-family)', outline: 'none', background: 'var(--wi-background-secondary)', color: 'var(--wi-text-primary)' },
  errorMsg: { fontSize: 12, color: '#EF4444', margin: '4px 0 0' },
  successMsg: { fontSize: 12, color: '#22C55E', margin: '4px 0 0' },
  noticeMsg: { fontSize: 12, color: 'var(--wi-text-secondary)', margin: '4px 0 0' },
  // Content reader modal
  readerModal: { background: 'var(--wi-background-primary)', borderRadius: 16, width: '90%', maxWidth: 800, height: '85vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--wi-divider)' },
  readerIframe: { flex: 1, width: '100%', border: 'none', borderRadius: '0 0 16px 16px' },
  // Mode toggle (catalog / custom URL)
  modeToggle: { display: 'flex', gap: 0, marginBottom: 12 },
  modeTab: (active) => ({ padding: '6px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--wi-divider)', background: active ? 'var(--wi-action-primary)' : 'var(--wi-background-secondary)', color: active ? 'var(--wi-on-action-primary)' : 'var(--wi-text-secondary)', fontFamily: 'var(--wi-font-family)', borderRadius: 0, outline: 'none' }),
  // Chapter strip
  chapterStrip: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, padding: '10px 0', borderBottom: '1px solid var(--wi-divider)' },
  // Added/active → solid border + light bg. Not added → dashed, muted. Complete → green.
  chapterPill: (color, state) => {
    const borderStyle = state === 'empty' ? 'dashed' : 'solid';
    const borderColor = state === 'complete' ? '#22C55E' : color;
    const bg = state === 'complete' ? '#22C55E14' : state === 'active' ? color + '14' : 'transparent';
    const textColor = state === 'complete' ? '#16A34A' : state === 'active' ? color : 'var(--wi-text-secondary)';
    return {
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 4px 4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      border: `1.5px ${borderStyle} ${borderColor}`, background: bg, color: textColor,
      transition: 'all 150ms ease', fontFamily: 'var(--wi-font-family)', whiteSpace: 'nowrap',
    };
  },
  chapterProgress: { fontSize: 10, opacity: 0.7, fontVariantNumeric: 'tabular-nums' },
  // Discreet ghost button — colored glyph, no fill. Tinted background appears on hover.
  chapterIconBtn: (color) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 16, height: 16, borderRadius: '50%', border: 'none', cursor: 'pointer',
    background: 'transparent', color, fontSize: 13, fontWeight: 600, lineHeight: 1,
    fontFamily: 'var(--wi-font-family)', padding: 0, opacity: 0.55,
    transition: 'opacity 120ms ease, background 120ms ease',
  }),
  chapterIconBtnHover: (color) => ({ opacity: 1, background: color + '22' }),
  chapterTooltip: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 20,
    background: 'var(--wi-background-secondary)', border: '1px solid var(--wi-divider)',
    borderRadius: 6, padding: '6px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: 11, fontWeight: 500, color: 'var(--wi-text-primary)',
    whiteSpace: 'nowrap', fontFamily: 'var(--wi-font-family)', pointerEvents: 'none',
  },
  statsProgress: { height: 4, background: 'var(--wi-background-primary)', borderRadius: 2, overflow: 'hidden', marginTop: 6 },
  // Custom form
  formGroup: { marginBottom: 12 },
  formLabel: { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--wi-text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' },
  formInput: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--wi-divider)', fontSize: 13, fontFamily: 'var(--wi-font-family)', outline: 'none', background: 'var(--wi-background-secondary)', color: 'var(--wi-text-primary)', boxSizing: 'border-box' },
};

/* ── ContentReaderModal ───────────────────────────────────────── */

function ContentReaderModal({ title, url, isCustom, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Content modules are designed for mobile (max-width 480px).
  // We render the iframe in a phone-sized container centered in the modal.
  const isCatalogContent = !isCustom;

  return createPortal((
    <div style={s.overlay} onClick={onClose}>
      <div style={s.readerModal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--wi-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
            {isCustom && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#6B728018', color: '#6B7280' }}>Custom</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--wi-action-primary)', textDecoration: 'none' }}>
              Open in tab &#8599;
            </a>
            <button style={s.modalClose} onClick={onClose}>&times;</button>
          </div>
        </div>
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', justifyContent: 'center', background: isCatalogContent ? '#f0f0f0' : 'transparent' }}>
          {loading && !error && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--wi-text-secondary)', fontSize: 13, zIndex: 1 }}>
              Loading content...
            </div>
          )}
          {error ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, color: 'var(--wi-text-secondary)' }}>
              <span style={{ fontSize: 13 }}>This content cannot be previewed inline.</span>
              <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--wi-action-primary)' }}>Open in new tab &#8599;</a>
            </div>
          ) : (
            <div style={{
              width: isCatalogContent ? 420 : '100%',
              maxWidth: '100%',
              height: '100%',
              background: 'var(--wi-background-secondary)',
              borderRadius: isCatalogContent ? '0 0 12px 12px' : '0 0 16px 16px',
              boxShadow: isCatalogContent ? '0 0 20px rgba(0,0,0,0.08)' : 'none',
              overflow: 'hidden',
            }}>
              <iframe
                src={url}
                style={{ width: '100%', height: '100%', border: 'none', opacity: loading ? 0 : 1, transition: 'opacity 200ms ease' }}
                sandbox={isCustom ? 'allow-scripts allow-same-origin' : undefined}
                onLoad={() => setLoading(false)}
                onError={() => { setLoading(false); setError(true); }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}

/* ── ContentTaskRow ────────────────────────────────────────────── */

function ContentTaskRow({ task, onCancel, onReactivate, cancelling, reactivating, onRead, isDraggable, dragHandlers, isDropTarget, dropPosition }) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const ctx = parseCtx(task);
  const track = ctx.track || task.clinical_domain || 'education';
  const format = ctx.format || '';
  const status = task.status || 'scheduled';
  const sm = STATUS_META[status] || STATUS_META.scheduled;
  const isCancellable = ['scheduled', 'pending'].includes(status);
  const isCancelled = status === 'cancelled';
  const contentUrl = getContentUrl(task);
  const isCustom = ctx.custom === true;
  const isReactivating = reactivating === task.task_id;

  return (
    <>
      {isDropTarget && dropPosition === 'before' && <div style={s.dropIndicator} />}
      <div
        style={{
          ...s.row,
          ...(dragHandlers?.isDragging ? { opacity: 0.4 } : {}),
          ...(isDropTarget ? { background: 'var(--wi-background-info)' } : {}),
        }}
        draggable={isDraggable}
        onDragStart={dragHandlers?.onDragStart}
        onDragEnd={dragHandlers?.onDragEnd}
        onDragOver={dragHandlers?.onDragOver}
        onDragLeave={dragHandlers?.onDragLeave}
        onDrop={dragHandlers?.onDrop}
      >
        <span style={{ ...s.dragHandle, visibility: isDraggable ? 'visible' : 'hidden' }} title={isDraggable ? 'Drag to reorder' : undefined}>&#x2801;&#x2801;</span>
        <div style={s.dateCol}>{fmtDate(task.available_at || task.due_at)}</div>
        <span style={s.trackBadge(trackColor(track))}>{track}</span>
        <div style={isCancelled ? s.titleCancelled : s.title} title={task.title}>
          {task.title?.replace(/^Read:\s*/, '') || ctx.title || 'Untitled'}
          {isCustom && <span style={{ fontSize: 9, marginLeft: 4, color: '#6B7280' }}>(custom)</span>}
        </div>
        {format && <span style={s.formatBadge}>{format}</span>}
        {contentUrl && (
          <button style={s.readBtn} onClick={(e) => { e.stopPropagation(); onRead(task); }}>Read</button>
        )}
        <span style={s.statusBadge(sm.color)}>{sm.icon} {sm.label}</span>
        {isCancellable && !confirmCancel && (
          <button style={s.cancelBtn} onClick={() => setConfirmCancel(true)}>Cancel</button>
        )}
        {isCancellable && confirmCancel && (
          <div style={s.cancelConfirm}>
            <button style={s.confirmBtn} disabled={cancelling === task.task_id} onClick={() => onCancel(task.task_id)}>
              {cancelling === task.task_id ? '...' : 'Confirm'}
            </button>
            <button style={s.cancelBtn} onClick={() => setConfirmCancel(false)}>No</button>
          </div>
        )}
        {isCancelled && onReactivate && (
          <button
            style={s.readBtn}
            disabled={isReactivating}
            onClick={() => onReactivate(task.task_id)}
            title="Restore this content to the patient's curriculum"
          >
            {isReactivating ? '...' : 'Restore'}
          </button>
        )}
      </div>
      {isDropTarget && dropPosition === 'after' && <div style={s.dropIndicator} />}
    </>
  );
}

/* ── ContentCatalogPicker ──────────────────────────────────────── */

function ContentCatalogPicker({ patient, carePeriod: _carePeriod, existingSlugs, tasks, onSchedule, onClose, onPreview }) {
  const catalogQuery = useContentCatalog();
  const catalog = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const loading = catalogQuery.isLoading;
  const scheduleMut = useScheduleContentTask();
  const scheduleCustomMut = useScheduleCustomContentTask();

  const [search, setSearch] = useState('');
  const [trackFilter, setTrackFilter] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [notice, setNotice] = useState('');
  const [mode, setMode] = useState('catalog');

  const [customTitle, setCustomTitle] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [customTrack, setCustomTrack] = useState('');
  const [customFormat, setCustomFormat] = useState('article');

  const tracks = useMemo(() => [...new Set(catalog.map(c => c.track))].sort(), [catalog]);
  const phases = useMemo(() => [...new Set(catalog.map(c => c.phase))].sort((a, b) => a - b), [catalog]);

  const filtered = useMemo(() => {
    return catalog.filter(item => {
      if (trackFilter && item.track !== trackFilter) return false;
      if (phaseFilter && item.phase !== Number(phaseFilter)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!item.title.toLowerCase().includes(q) && !item.hook?.toLowerCase().includes(q) && !item.slug.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [catalog, trackFilter, phaseFilter, search]);

  async function handleSchedule() {
    if (!selected) return;
    setScheduling(true);
    setError('');
    setSuccess('');
    setNotice('');
    try {
      const nextDate = computeNextSlotDate(tasks);
      const result = await scheduleMut.mutateAsync({
        withingsUserId: patient.userid,
        catalogId: selected.catalog_id,
        scheduledDate: nextDate,
      });
      const outcome = interpretScheduleResult(result, selected.title);
      setScheduling(false);
      if (outcome.kind === 'success') {
        setSuccess(outcome.message);
        setSelected(null);
        onSchedule(result);
      } else if (outcome.kind === 'notice') {
        setNotice(outcome.message);
      } else {
        setError(outcome.message);
      }
    } catch (e) {
      setError(e.message);
      setScheduling(false);
    }
  }

  async function handleCustomSchedule() {
    if (!customTitle.trim() || !customUrl.trim()) return;
    // Validate URL
    try { new URL(customUrl); } catch { setError('Please enter a valid URL'); return; }
    setScheduling(true);
    setError('');
    setSuccess('');
    setNotice('');
    try {
      const nextDate = computeNextSlotDate(tasks);
      const label = customTitle.trim();
      const result = await scheduleCustomMut.mutateAsync({
        withingsUserId: patient.userid,
        customContent: {
          title: label,
          url: customUrl.trim(),
          track: customTrack || 'custom',
          format: customFormat,
        },
        scheduledDate: nextDate,
      });
      const outcome = interpretScheduleResult(result, label);
      setScheduling(false);
      if (outcome.kind === 'success') {
        setSuccess(outcome.message);
        setCustomTitle('');
        setCustomUrl('');
        setCustomTrack('');
        setCustomFormat('article');
        onSchedule(result);
      } else if (outcome.kind === 'notice') {
        setNotice(outcome.message);
      } else {
        setError(outcome.message);
      }
    } catch (e) {
      setError(e.message);
      setScheduling(false);
    }
  }

  return createPortal((
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--wi-text-primary)' }}>Add Content</span>
          <button style={s.modalClose} onClick={onClose}>&times;</button>
        </div>

        {/* Mode toggle */}
        <div style={{ padding: '12px 20px 0' }}>
          <div style={s.modeToggle}>
            <button
              style={{ ...s.modeTab(mode === 'catalog'), borderRadius: '6px 0 0 6px' }}
              onClick={() => { setMode('catalog'); setError(''); setSuccess(''); setNotice(''); }}
            >
              Catalog
            </button>
            <button
              style={{ ...s.modeTab(mode === 'custom'), borderRadius: '0 6px 6px 0' }}
              onClick={() => { setMode('custom'); setError(''); setSuccess(''); setNotice(''); }}
            >
              Custom URL
            </button>
          </div>
        </div>

        {mode === 'catalog' ? (
          <>
            {/* Filters */}
            <div style={{ padding: '0 20px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                style={s.searchInput}
                placeholder="Search by title..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <select style={s.select} value={trackFilter} onChange={e => setTrackFilter(e.target.value)}>
                <option value="">All tracks</option>
                {tracks.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select style={s.select} value={phaseFilter} onChange={e => setPhaseFilter(e.target.value)}>
                <option value="">All phases</option>
                {phases.map(p => <option key={p} value={p}>{p} — {PHASE_LABELS[p] || ''}</option>)}
              </select>
            </div>

            {/* Catalog list */}
            <div style={s.modalBody}>
              {loading ? (
                <p style={{ fontSize: 13, color: 'var(--wi-text-secondary)' }}>Loading catalog...</p>
              ) : filtered.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--wi-text-secondary)' }}>No content found</p>
              ) : (
                filtered.map(item => {
                  const alreadyScheduled = existingSlugs.has(item.slug);
                  if (alreadyScheduled) {
                    return (
                      <div key={item.catalog_id} style={s.catalogItemDisabled}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={s.trackBadge(trackColor(item.track))}>{item.track}</span>
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--wi-text-secondary)' }}>{item.title}</span>
                          <span style={{ fontSize: 10, color: '#22C55E', fontWeight: 600, marginLeft: 'auto' }}>Scheduled</span>
                        </div>
                      </div>
                    );
                  }
                  const isSel = selected?.catalog_id === item.catalog_id;
                  return (
                    <div key={item.catalog_id} style={s.catalogItem(isSel)} onClick={() => setSelected(item)}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                        <span style={s.trackBadge(trackColor(item.track))}>{item.track}</span>
                        <span style={{ fontSize: 10, color: 'var(--wi-text-secondary)' }}>Phase {item.phase} — {PHASE_LABELS[item.phase] || ''}</span>
                        <span style={s.formatBadge}>{item.format}</span>
                        <button
                          style={{ ...s.readBtn, marginLeft: 'auto', fontSize: 10, padding: '2px 6px' }}
                          onClick={(e) => { e.stopPropagation(); onPreview(item.title, getContentUrlFromCatalog(item)); }}
                        >
                          Preview
                        </button>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--wi-text-primary)' }}>{item.title}</div>
                      {item.hook && <div style={{ fontSize: 11, color: 'var(--wi-text-secondary)', marginTop: 2 }}>{item.hook}</div>}
                    </div>
                  );
                })
              )}
            </div>

            {/* Add button (no date picker) */}
            <div style={s.modalFooter}>
              {selected && (
                <span style={{ fontSize: 12, color: 'var(--wi-text-primary)', fontWeight: 500, flex: 1 }}>
                  {selected.title}
                </span>
              )}
              <button
                style={selected && !scheduling ? s.addBtn : s.addBtnDisabled}
                disabled={!selected || scheduling}
                onClick={handleSchedule}
              >
                {scheduling ? 'Adding...' : 'Add to curriculum'}
              </button>
              {error && <p style={s.errorMsg}>{error}</p>}
              {success && <p style={s.successMsg}>{success}</p>}
              {notice && <p style={s.noticeMsg}>{notice}</p>}
            </div>
          </>
        ) : (
          /* Custom URL form */
          <>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.formLabel}>Title *</label>
                <input
                  style={s.formInput}
                  placeholder="e.g., Understanding GLP-1 Medications"
                  value={customTitle}
                  onChange={e => setCustomTitle(e.target.value)}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.formLabel}>URL *</label>
                <input
                  style={s.formInput}
                  placeholder="https://..."
                  value={customUrl}
                  onChange={e => { setCustomUrl(e.target.value); setError(''); }}
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...s.formGroup, flex: 1 }}>
                  <label style={s.formLabel}>Track</label>
                  <select style={{ ...s.formInput, padding: '7px 8px' }} value={customTrack} onChange={e => setCustomTrack(e.target.value)}>
                    <option value="">Custom</option>
                    {Object.keys(TRACK_COLORS).filter(t => t !== 'custom').map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div style={{ ...s.formGroup, flex: 1 }}>
                  <label style={s.formLabel}>Format</label>
                  <select style={{ ...s.formInput, padding: '7px 8px' }} value={customFormat} onChange={e => setCustomFormat(e.target.value)}>
                    <option value="article">Article</option>
                    <option value="video">Video</option>
                    <option value="pdf">PDF</option>
                    <option value="tool">Interactive tool</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button
                style={customTitle.trim() && customUrl.trim() && !scheduling ? s.addBtn : s.addBtnDisabled}
                disabled={!customTitle.trim() || !customUrl.trim() || scheduling}
                onClick={handleCustomSchedule}
              >
                {scheduling ? 'Adding...' : 'Add to curriculum'}
              </button>
              {error && <p style={s.errorMsg}>{error}</p>}
              {success && <p style={s.successMsg}>{success}</p>}
              {notice && <p style={s.noticeMsg}>{notice}</p>}
            </div>
          </>
        )}
      </div>
    </div>
  ), document.body);
}

/* ── ChapterStrip ─────────────────────────────────────────────── */

function ChapterStrip({ tasks, patient, onRefresh }) {
  const catalogQuery = useContentCatalog();
  const catalog = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const loaded = catalogQuery.isSuccess;
  const scheduleMut = useScheduleContentTask();
  const cancelMut = useCancelContentTask();

  const [busyTrack, setBusyTrack] = useState(null);
  const [progress, setProgress] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [hoverTarget, setHoverTarget] = useState(null);

  // Unified per-track stats: catalog size + scheduled/completed/pending counts + unscheduled items
  const trackStats = useMemo(() => computeTrackCatalogStats(catalog, tasks), [catalog, tasks]);

  // Sort: in-progress first, then empty (inviting to add), then completed
  const orderedTracks = useMemo(() => {
    const keys = Object.keys(trackStats);
    return keys.sort((a, b) => {
      const sa = trackStats[a], sb = trackStats[b];
      const rank = (s) => {
        if (s.scheduled === 0) return 2; // empty chapters — mid
        if (s.catalogTotal > 0 && s.completed === s.catalogTotal) return 3; // done — last
        return 1; // active — first
      };
      const r = rank(sa) - rank(sb);
      if (r !== 0) return r;
      return a.localeCompare(b);
    });
  }, [trackStats]);

  // Active slugs (not cancelled) — used to skip already-scheduled items on Add
  const activeSlugs = useMemo(() => {
    const set = new Set();
    for (const t of tasks) {
      if (t.status === 'cancelled') continue;
      const slug = parseCtx(t).slug;
      if (slug) set.add(slug);
    }
    return set;
  }, [tasks]);

  async function handleAddChapter(trackName) {
    setBusyTrack(trackName);
    setProgress(`Adding ${trackName}...`);
    try {
      const toSchedule = catalog
        .filter(c => c.track === trackName && !activeSlugs.has(c.slug))
        .sort((a, b) => (a.phase - b.phase) || ((a.sort_order || 0) - (b.sort_order || 0)));
      if (toSchedule.length === 0) {
        setProgress(`All ${trackName} content already scheduled`);
        setTimeout(() => { setProgress(''); setBusyTrack(null); }, 1500);
        return;
      }

      let scheduled = 0;
      for (const item of toSchedule) {
        scheduled++;
        setProgress(`Adding ${trackName} (${scheduled}/${toSchedule.length})...`);
        const nextDate = computeNextSlotDate(tasks, scheduled - 1);
        try {
          await scheduleMut.mutateAsync({
            withingsUserId: patient.userid,
            catalogId: item.catalog_id,
            scheduledDate: nextDate,
          });
        } catch (e) {
          if (!e.message?.includes('already scheduled')) throw e;
        }
      }
      setProgress(`Added ${scheduled} ${trackName} items`);
      onRefresh();
      setTimeout(() => { setProgress(''); setBusyTrack(null); }, 1200);
    } catch (e) {
      setProgress(`Error: ${e.message}`);
      setTimeout(() => { setProgress(''); setBusyTrack(null); }, 2000);
    }
  }

  async function handleRemoveChapter(trackName) {
    setBusyTrack(trackName);
    setConfirmRemove(null);
    setProgress(`Removing ${trackName}...`);
    try {
      const toCancel = tasks.filter(t =>
        ['scheduled', 'pending'].includes(t.status) && parseCtx(t).track === trackName
      );
      let cancelled = 0;
      for (const t of toCancel) {
        cancelled++;
        setProgress(`Removing ${trackName} (${cancelled}/${toCancel.length})...`);
        await cancelMut.mutateAsync({ taskId: t.task_id, withingsUserId: patient.userid });
      }
      setProgress(`Removed ${cancelled} ${trackName} items`);
      onRefresh();
      setTimeout(() => { setProgress(''); setBusyTrack(null); }, 1200);
    } catch (e) {
      setProgress(`Error: ${e.message}`);
      setTimeout(() => { setProgress(''); setBusyTrack(null); }, 2000);
    }
  }

  if (!loaded || orderedTracks.length === 0) return null;

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--wi-text-secondary)', marginBottom: 6 }}>
        Chapters
      </div>
      <div style={s.chapterStrip}>
        {orderedTracks.map(track => {
          const color = trackColor(track);
          const st = trackStats[track];
          const isBusy = busyTrack === track;
          const catalogTotal = st.catalogTotal;
          const isAdded = st.scheduled > 0;
          const isComplete = isAdded && st.completed === st.scheduled;
          const state = isComplete ? 'complete' : isAdded ? 'active' : 'empty';

          // Binary action: if chapter has no scheduled tasks → + Add; otherwise → × Remove.
          // (Partially-scheduled chapters show × — cleaner mental model than two buttons.)
          const canAdd = !isAdded && catalogTotal > 0;
          const canRemove = st.pending > 0;

          return (
            <div key={track} style={{ position: 'relative', display: 'inline-flex' }}>
              <div style={s.chapterPill(color, state)}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: isComplete ? '#22C55E' : color, flexShrink: 0 }} />
                <span>{track}</span>
                {catalogTotal > 0 && (
                  <span style={s.chapterProgress}>
                    {isComplete ? '\u2713 ' : ''}{st.scheduled}/{catalogTotal}
                  </span>
                )}
                {isBusy ? (
                  <span style={{ fontSize: 10, opacity: 0.7, margin: '0 4px' }}>...</span>
                ) : canAdd ? (
                  <button
                    style={{
                      ...s.chapterIconBtn('#16A34A'),
                      ...(hoverTarget?.track === track && hoverTarget.action === 'add' ? s.chapterIconBtnHover('#16A34A') : {}),
                    }}
                    onClick={() => handleAddChapter(track)}
                    onMouseEnter={() => setHoverTarget({ track, action: 'add' })}
                    onMouseLeave={() => setHoverTarget(null)}
                    aria-label={`Add ${catalogTotal} ${track} modules`}
                  >+</button>
                ) : canRemove ? (
                  <button
                    style={{
                      ...s.chapterIconBtn('#DC2626'),
                      ...(hoverTarget?.track === track && hoverTarget.action === 'remove' ? s.chapterIconBtnHover('#DC2626') : {}),
                    }}
                    onClick={() => setConfirmRemove(track)}
                    onMouseEnter={() => setHoverTarget({ track, action: 'remove' })}
                    onMouseLeave={() => setHoverTarget(null)}
                    aria-label={`Remove ${track}`}
                  >&times;</button>
                ) : null}
              </div>

              {/* One-line tooltips */}
              {hoverTarget?.track === track && hoverTarget.action === 'add' && !confirmRemove && (
                <div style={s.chapterTooltip}>Add {catalogTotal} {track} modules</div>
              )}
              {hoverTarget?.track === track && hoverTarget.action === 'remove' && !confirmRemove && (
                <div style={s.chapterTooltip}>
                  Remove {track}
                  {st.completed > 0 ? ` — keep ${st.completed} already read` : ''}
                </div>
              )}

              {/* Confirm remove */}
              {confirmRemove === track && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 10,
                  background: 'var(--wi-background-secondary)', border: '1px solid var(--wi-divider)',
                  borderRadius: 8, padding: '10px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  minWidth: 240, fontFamily: 'var(--wi-font-family)',
                }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--wi-text-primary)', margin: '0 0 4px' }}>
                    Remove {track}?
                  </p>
                  <p style={{ fontSize: 11, color: 'var(--wi-text-secondary)', margin: '0 0 10px' }}>
                    Cancels {st.pending} upcoming item{st.pending === 1 ? '' : 's'}.
                    {st.completed > 0 && ` ${st.completed} already read will be kept.`}
                  </p>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={s.confirmBtn} onClick={() => handleRemoveChapter(track)}>Remove</button>
                    <button style={s.cancelBtn} onClick={() => setConfirmRemove(null)}>Keep</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {progress && (
        <div style={{ fontSize: 11, color: 'var(--wi-text-secondary)', marginBottom: 8, fontStyle: 'italic' }}>
          {progress}
        </div>
      )}
    </div>
  );
}

/* ── ContentCurriculumEditor (main export) ─────────────────────── */

export default function ContentCurriculumEditor({ patient, carePeriod }) {
  const qc = useQueryClient();
  const tasksQuery = useContentTasks(patient?.userid);
  const tasks = useMemo(() => tasksQuery.data ?? [], [tasksQuery.data]);
  const loading = tasksQuery.isLoading;
  const loadTasks = useCallback(() => tasksQuery.refetch(), [tasksQuery]);
  const cancelMut = useCancelContentTask();
  const reactivateMut = useReactivateContentTask();
  const rescheduleMut = useRescheduleContentTask();

  // Optimistic mutation of the patient's content-tasks cache. n8n cloud
  // serializes WF17 under burst (rapid clicks, "Add all"), so a refetch
  // races the queued event and re-paints the stale row.
  const patchTaskInCache = useCallback((taskId, patch) => {
    if (!patient?.userid) return;
    qc.setQueryData(TASK_KEYS.contentForPatient(patient.userid), (prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((t) => (t.task_id === taskId ? { ...t, ...patch } : t));
    });
  }, [qc, patient?.userid]);

  const [cancelling, setCancelling] = useState(null);
  const [reactivating, setReactivating] = useState(null);
  const [reactivateError, setReactivateError] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [trackFilter, setTrackFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [readerTask, setReaderTask] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);

  // Drag state
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [dropPosition, setDropPosition] = useState(null);
  const [reordering, setReordering] = useState(false);
  const dragCounter = useRef(0);

  /* ── Computed ── */

  const existingSlugs = useMemo(() => {
    const set = new Set();
    for (const t of tasks) {
      const ctx = parseCtx(t);
      if (ctx.slug && t.status !== 'cancelled') set.add(ctx.slug);
    }
    return set;
  }, [tasks]);

  const tracks = useMemo(() => [...new Set(tasks.map(t => parseCtx(t).track).filter(Boolean))].sort(), [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      if (trackFilter) {
        const ctx = parseCtx(t);
        if (ctx.track !== trackFilter) return false;
      }
      if (statusFilter && t.status !== statusFilter) return false;
      return true;
    });
  }, [tasks, trackFilter, statusFilter]);

  // Flat sorted list for drag-to-reorder
  const sortedTasks = useMemo(() => {
    return [...filteredTasks].sort((a, b) => new Date(a.available_at || a.due_at) - new Date(b.available_at || b.due_at));
  }, [filteredTasks]);

  const grouped = useMemo(() => groupByMonth(sortedTasks), [sortedTasks]);
  const months = Object.keys(grouped);

  const stats = useMemo(() => {
    const s = { completed: 0, scheduled: 0, pending: 0, in_progress: 0, cancelled: 0, total: tasks.length };
    for (const t of tasks) s[t.status] = (s[t.status] || 0) + 1;
    return s;
  }, [tasks]);

  /* ── Handlers ── */

  async function handleCancel(taskId) {
    setCancelling(taskId);
    try {
      await cancelMut.mutateAsync({ taskId, withingsUserId: patient.userid });
      patchTaskInCache(taskId, { status: 'cancelled' });
    } catch (e) {
      console.error('Cancel failed:', e);
    }
    setCancelling(null);
  }

  async function handleReactivate(taskId) {
    setReactivating(taskId);
    setReactivateError(null);
    try {
      await reactivateMut.mutateAsync({ taskId, withingsUserId: patient.userid });
      patchTaskInCache(taskId, { status: 'scheduled' });
    } catch (e) {
      setReactivateError(e?.message || 'Restore failed');
    }
    setReactivating(null);
  }

  function handleScheduled() {
    loadTasks();
    setShowPicker(false);
  }

  function handleRead(task) {
    const ctx = parseCtx(task);
    const url = getContentUrl(task);
    if (url) {
      setReaderTask({ title: task.title?.replace(/^Read:\s*/, '') || ctx.title || 'Content', url, isCustom: ctx.custom === true });
    }
  }

  function handlePreview(title, url) {
    if (url) setPreviewContent({ title, url });
  }

  /* ── Drag handlers ── */

  function makeDragHandlers(task, _index) {
    const isDraggable = ['scheduled', 'pending'].includes(task.status) && !reordering;
    if (!isDraggable) return { isDraggable: false };

    return {
      isDraggable: true,
      isDragging: draggedTaskId === task.task_id,
      onDragStart: (e) => {
        setDraggedTaskId(task.task_id);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', task.task_id);
      },
      onDragEnd: () => {
        setDraggedTaskId(null);
        setDropTargetId(null);
        setDropPosition(null);
        dragCounter.current = 0;
      },
      onDragOver: (e) => {
        if (!draggedTaskId || draggedTaskId === task.task_id) return;
        if (!['scheduled', 'pending'].includes(task.status)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        setDropTargetId(task.task_id);
        setDropPosition(e.clientY < midY ? 'before' : 'after');
      },
      onDragLeave: (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        if (dropTargetId === task.task_id) {
          setDropTargetId(null);
          setDropPosition(null);
        }
      },
      onDrop: async (e) => {
        e.preventDefault();
        if (!draggedTaskId || draggedTaskId === task.task_id || reordering) return;

        const draggedIdx = sortedTasks.findIndex(t => t.task_id === draggedTaskId);
        const targetIdx = sortedTasks.findIndex(t => t.task_id === task.task_id);
        if (draggedIdx === -1 || targetIdx === -1) return;

        const insertIdx = dropPosition === 'before' ? targetIdx : targetIdx + 1;
        const beforeTask = insertIdx > 0 ? sortedTasks[insertIdx - 1] : null;
        const afterTask = insertIdx < sortedTasks.length ? sortedTasks[insertIdx] : null;

        // Don't use the dragged task itself as a neighbor
        const effectiveBefore = beforeTask?.task_id === draggedTaskId
          ? (insertIdx > 1 ? sortedTasks[insertIdx - 2] : null)
          : beforeTask;
        const effectiveAfter = afterTask?.task_id === draggedTaskId
          ? (insertIdx + 1 < sortedTasks.length ? sortedTasks[insertIdx + 1] : null)
          : afterTask;

        const newDate = computeDropDate(effectiveBefore, effectiveAfter);

        // Optimistic UI via TanStack cache. Snapshot the current cache
        // so we can roll back on mutation failure.
        const cacheKey = TASK_KEYS.contentForPatient(patient.userid);
        const previousCache = qc.getQueryData(cacheKey);
        patchTaskInCache(draggedTaskId, { available_at: newDate + 'T09:00:00Z' });

        setDraggedTaskId(null);
        setDropTargetId(null);
        setDropPosition(null);
        setReordering(true);

        try {
          await rescheduleMut.mutateAsync({ taskId: draggedTaskId, newDate, withingsUserId: patient.userid });
        } catch (e) {
          console.error('Reschedule failed:', e);
          qc.setQueryData(cacheKey, previousCache);
        }
        setReordering(false);
      },
    };
  }

  /* ── Render ── */

  if (loading) return <p className="detail1 text-secondary" style={{ margin: 0 }}>Loading curriculum...</p>;

  if (!carePeriod) {
    return (
      <div style={s.emptyState}>
        <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--wi-text-primary)', margin: '0 0 6px' }}>No active care period</p>
        <p style={{ margin: 0 }}>Content scheduling requires an active care period. Align this patient with CMS first.</p>
      </div>
    );
  }

  // Active content reader modal (from task row or catalog preview)
  const activeReader = readerTask || previewContent;

  return (
    <div>
      {/* Action bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button style={s.addBtn} onClick={() => setShowPicker(true)}>+ Add Content</button>
      </div>

      {reactivateError && (
        <div style={{
          marginBottom: 10, padding: '6px 10px', borderRadius: 6,
          background: 'var(--wi-background-bad)', color: 'var(--wi-status-bad)',
          fontSize: 12,
        }}>
          {reactivateError}
          <button
            style={{ marginLeft: 8, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 12 }}
            onClick={() => setReactivateError(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Stats — sentence form with overall progress */}
      {(() => {
        const upcoming = stats.scheduled + stats.pending;
        const totalActive = stats.completed + upcoming + stats.in_progress;
        const pct = totalActive > 0 ? Math.round((stats.completed / totalActive) * 100) : 0;
        if (totalActive === 0) return null;
        return (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', fontSize: 12, color: 'var(--wi-text-primary)' }}>
              <span><strong style={{ color: '#16A34A', fontWeight: 700 }}>{stats.completed}</strong> of <strong>{totalActive}</strong> read</span>
              {upcoming > 0 && (
                <>
                  <span style={{ color: 'var(--wi-text-secondary)' }}>·</span>
                  <span><strong style={{ color: '#2563EB', fontWeight: 700 }}>{upcoming}</strong> upcoming</span>
                </>
              )}
              {stats.in_progress > 0 && (
                <>
                  <span style={{ color: 'var(--wi-text-secondary)' }}>·</span>
                  <span><strong style={{ color: '#2563EB', fontWeight: 700 }}>{stats.in_progress}</strong> in progress</span>
                </>
              )}
              {stats.cancelled > 0 && (
                <>
                  <span style={{ color: 'var(--wi-text-secondary)' }}>·</span>
                  <span style={{ color: 'var(--wi-text-secondary)' }}>{stats.cancelled} cancelled</span>
                </>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--wi-text-secondary)', fontWeight: 600 }}>{pct}%</span>
            </div>
            <div style={s.statsProgress}>
              <div style={{
                height: '100%', width: `${pct}%`, borderRadius: 2,
                background: pct === 100 ? '#22C55E' : 'var(--wi-action-primary)',
                transition: 'width 300ms ease',
              }} />
            </div>
          </div>
        );
      })()}

      {/* Chapter strip */}
      <ChapterStrip
        tasks={tasks}
        patient={patient}
        onRefresh={loadTasks}
      />

      {/* Filters */}
      <div style={s.filterRow}>
        <select style={s.select} value={trackFilter} onChange={e => setTrackFilter(e.target.value)}>
          <option value="">All tracks</option>
          {tracks.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select style={s.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="completed">Completed</option>
          <option value="in_progress">In progress</option>
          <option value="pending">Pending</option>
          <option value="scheduled">Scheduled</option>
          <option value="cancelled">Cancelled</option>
        </select>
        {reordering && <span style={{ fontSize: 11, color: 'var(--wi-text-secondary)', fontStyle: 'italic' }}>Saving...</span>}
      </div>

      {/* Timeline */}
      {months.length === 0 ? (
        <div style={s.emptyState}>
          <p style={{ margin: 0 }}>No content tasks match the current filters</p>
        </div>
      ) : (
        months.map(month => (
          <div key={month}>
            <div style={s.monthHeader}>{month}</div>
            {grouped[month].map((t, idx) => {
              const handlers = makeDragHandlers(t, idx);
              return (
                <ContentTaskRow
                  key={t.task_id}
                  task={t}
                  onCancel={handleCancel}
                  onReactivate={handleReactivate}
                  cancelling={cancelling}
                  reactivating={reactivating}
                  onRead={handleRead}
                  isDraggable={handlers.isDraggable}
                  dragHandlers={handlers}
                  isDropTarget={dropTargetId === t.task_id}
                  dropPosition={dropPosition}
                />
              );
            })}
          </div>
        ))
      )}

      {/* Catalog picker modal */}
      {showPicker && (
        <ContentCatalogPicker
          patient={patient}
          carePeriod={carePeriod}
          existingSlugs={existingSlugs}
          tasks={tasks}
          onSchedule={handleScheduled}
          onClose={() => setShowPicker(false)}
          onPreview={handlePreview}
        />
      )}

      {/* Content reader modal */}
      {activeReader && (
        <ContentReaderModal
          title={activeReader.title}
          url={activeReader.url}
          isCustom={activeReader.isCustom || false}
          onClose={() => { setReaderTask(null); setPreviewContent(null); }}
        />
      )}
    </div>
  );
}
